import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Table, Badge, Button, Form, InputGroup } from "react-bootstrap";
import {
  FiEdit2,
  FiCheckCircle,
  FiXCircle,
  FiSearch,
  FiMonitor,
  FiPackage,
  FiFilter,
  FiList,
  FiShield,
  FiGlobe,
  FiTag,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import "./MonitorsTable.css";
import { getHost } from "../utils/utils";

// Convert ISO 3166-1 alpha-2 country code to flag emoji
const countryCodeToFlag = (countryCode) => {
  if (!countryCode) return null;
  // Special case for international/multi-region
  if (countryCode.toUpperCase() === "INT") return "\u{1F310}"; // Globe with meridians
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

// Valid ISO 3166-1 alpha-2 country codes + INT for international
const VALID_COUNTRY_CODES = new Set([
  "AD", "AE", "AF", "AG", "AI", "AL", "AM", "AO", "AQ", "AR", "AS", "AT", "AU", "AW", "AX", "AZ",
  "BA", "BB", "BD", "BE", "BF", "BG", "BH", "BI", "BJ", "BL", "BM", "BN", "BO", "BQ", "BR", "BS",
  "BT", "BV", "BW", "BY", "BZ", "CA", "CC", "CD", "CF", "CG", "CH", "CI", "CK", "CL", "CM", "CN",
  "CO", "CR", "CU", "CV", "CW", "CX", "CY", "CZ", "DE", "DJ", "DK", "DM", "DO", "DZ", "EC", "EE",
  "EG", "EH", "ER", "ES", "ET", "FI", "FJ", "FK", "FM", "FO", "FR", "GA", "GB", "GD", "GE", "GF",
  "GG", "GH", "GI", "GL", "GM", "GN", "GP", "GQ", "GR", "GS", "GT", "GU", "GW", "GY", "HK", "HM",
  "HN", "HR", "HT", "HU", "ID", "IE", "IL", "IM", "IN", "IO", "IQ", "IR", "IS", "IT", "JE", "JM",
  "JO", "JP", "KE", "KG", "KH", "KI", "KM", "KN", "KP", "KR", "KW", "KY", "KZ", "LA", "LB", "LC",
  "LI", "LK", "LR", "LS", "LT", "LU", "LV", "LY", "MA", "MC", "MD", "ME", "MF", "MG", "MH", "MK",
  "ML", "MM", "MN", "MO", "MP", "MQ", "MR", "MS", "MT", "MU", "MV", "MW", "MX", "MY", "MZ", "NA",
  "NC", "NE", "NF", "NG", "NI", "NL", "NO", "NP", "NR", "NU", "NZ", "OM", "PA", "PE", "PF", "PG",
  "PH", "PK", "PL", "PM", "PN", "PR", "PS", "PT", "PW", "PY", "QA", "RE", "RO", "RS", "RU", "RW",
  "SA", "SB", "SC", "SD", "SE", "SG", "SH", "SI", "SJ", "SK", "SL", "SM", "SN", "SO", "SR", "SS",
  "ST", "SV", "SX", "SY", "SZ", "TC", "TD", "TF", "TG", "TH", "TJ", "TK", "TL", "TM", "TN", "TO",
  "TR", "TT", "TV", "TW", "TZ", "UA", "UG", "UM", "US", "UY", "UZ", "VA", "VC", "VE", "VG", "VI",
  "VN", "VU", "WF", "WS", "YE", "YT", "ZA", "ZM", "ZW",
  "INT" // International / multi-region
]);

const isValidCountryCode = (code) => {
  if (!code) return true; // Empty is valid (clears the region)
  return VALID_COUNTRY_CODES.has(code.toUpperCase());
};

const API_MONITOR_REGION = (monitorId) =>
  `${getHost()}/api/monitors/${monitorId}/region`;
const API_MONITOR_CATEGORY = (monitorId) =>
  `${getHost()}/api/monitors/${monitorId}/category`;
const getToken = () => localStorage.getItem("jwt");

const CATEGORY_OPTIONS = ["Sneakers", "Alternative", "Ticket", "TCG"];

const PAGE_SIZE = 50;

const SEARCH_DEBOUNCE_MS = 250;

const MonitorRow = React.memo(({
  monitor,
  isSelected,
  onToggleSelect,
  canEditCategory,
  isSavingCategory,
  onCategorySave,
  canEditRegion,
  onRegionEdit,
  regionEditor,
  onEdit,
  onWhitelist,
  onBlacklist,
}) => (
  <tr className="monitor-row">
    <td className="align-middle text-center">
      <input
        type="checkbox"
        className="monitor-checkbox"
        checked={isSelected}
        onChange={() => onToggleSelect(monitor._id)}
      />
    </td>
    <td className="fw-500 fs-14 text-white align-middle">
      <div className="d-flex align-items-center gap-2">
        <div
          className="status-dot"
          style={{
            backgroundColor:
              monitor.status === "working" ? "#00E98E" : "#FF577B",
          }}
        />
        {monitor.name}
      </div>
    </td>
    <td className="fw-500 fs-13 text-white-65 align-middle">
      {canEditCategory ? (
        <Form.Select
          size="sm"
          value={monitor.category || ""}
          onChange={(e) => onCategorySave(monitor._id, e.target.value)}
          disabled={isSavingCategory}
          className="bg-dark text-white border-secondary"
          style={{ width: "130px", fontSize: "13px", padding: "2px 6px" }}
        >
          {CATEGORY_OPTIONS.map((cat) => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </Form.Select>
      ) : (
        monitor.category || "Not set"
      )}
    </td>
    <td className="fw-500 fs-14 align-middle">
      <Badge
        bg={monitor.status === "working" ? "success" : "danger"}
        className="status-badge d-inline-flex align-items-center gap-1 px-2 py-1"
        style={{
          backgroundColor:
            monitor.status === "working" ? "#00E98E20" : "#FF577B20",
          color: monitor.status === "working" ? "#00E98E" : "#FF577B",
          border: `1px solid ${monitor.status === "working" ? "#00E98E40" : "#FF577B40"}`,
        }}
      >
        {monitor.status === "working" ? (
          <>
            <FiCheckCircle className="fs-12" />
            Active
          </>
        ) : (
          <>
            <FiXCircle className="fs-12" />
            Inactive
          </>
        )}
      </Badge>
    </td>
    <td className="fw-500 fs-14 align-middle">
      {regionEditor ? (
        <div className="d-flex align-items-center gap-2">
          <Form.Control
            type="text"
            value={regionEditor.value}
            onChange={(e) => regionEditor.onChange(e.target.value)}
            onKeyDown={(e) => regionEditor.onKeyDown(e, monitor._id)}
            onBlur={() => regionEditor.onSave(monitor._id)}
            placeholder="US"
            maxLength={3}
            autoFocus
            disabled={regionEditor.saving}
            className={`region-input ${regionEditor.error ? "is-invalid" : ""}`}
            style={{ width: "55px", padding: "2px 6px", fontSize: "13px" }}
          />
          {regionEditor.value && isValidCountryCode(regionEditor.value) && (
            <span className="fs-18">{countryCodeToFlag(regionEditor.value)}</span>
          )}
          {regionEditor.error && (
            <span className="text-danger fs-11">{regionEditor.error}</span>
          )}
        </div>
      ) : (
        <div
          className={`d-flex align-items-center gap-2 ${canEditRegion ? "region-editable" : ""}`}
          onClick={() => canEditRegion && onRegionEdit(monitor)}
          title={canEditRegion ? "Click to edit" : ""}
        >
          {monitor.region ? (
            <>
              <span className="fs-20">{countryCodeToFlag(monitor.region)}</span>
              <span className="text-white-65 fs-12">{monitor.region}</span>
            </>
          ) : (
            <span className="text-white-65 fs-13">
              {canEditRegion ? "Set region" : "—"}
            </span>
          )}
        </div>
      )}
    </td>
    <td className="text-end align-middle">
      <Button
        variant=""
        size="sm"
        className="fs-15 text-gray fw-500 edit-btn"
        onClick={() => onEdit(monitor)}
      >
        <FiEdit2 className="fs-18" />
      </Button>
      <Button
        variant=""
        size="sm"
        className="fs-15 text-gray fw-500 edit-btn"
        onClick={() => onWhitelist(monitor)}
        title="Manage PID whitelist"
        disabled={!monitor.supports_whitelist}
      >
        <FiList className="fs-18" />
      </Button>
      <Button
        variant=""
        size="sm"
        className="fs-15 text-gray fw-500 edit-btn"
        onClick={() => onBlacklist(monitor)}
        title="Manage PID blacklist"
      >
        <FiShield className="fs-18" />
      </Button>
    </td>
  </tr>
));

const MonitorsTable = ({
  monitors,
  loading,
  onEdit,
  onWhitelist,
  onBlacklist,
  canEditCategory,
  onCategoryUpdate,
  canEditRegion,
  onRegionUpdate,
  selectedCategory,
  setSelectedCategory,
  categories,
  selectedRegion,
  setSelectedRegion,
  regions,
  selectedMonitors,
  onSelectionChange,
  onOpenCategoryDefaults,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showRegionDropdown, setShowRegionDropdown] = useState(false);
  const [savingCategory, setSavingCategory] = useState(null);
  const [editingRegion, setEditingRegion] = useState(null); // monitorId being edited
  const [regionValue, setRegionValue] = useState("");
  const [regionError, setRegionError] = useState("");
  const [savingRegion, setSavingRegion] = useState(false);

  const handleCategorySave = useCallback(async (monitorId, newCategory) => {
    setSavingCategory(monitorId);
    try {
      const res = await fetch(API_MONITOR_CATEGORY(monitorId), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ category: newCategory }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to update");
      if (onCategoryUpdate) onCategoryUpdate(monitorId, data.category);
    } catch (error) {
      console.error("Failed to update category:", error);
    } finally {
      setSavingCategory(null);
    }
  }, [onCategoryUpdate]);

  const handleRegionEdit = useCallback((monitor) => {
    setEditingRegion(monitor._id);
    setRegionValue(monitor.region || "");
    setRegionError("");
  }, []);

  const handleRegionCancel = useCallback(() => {
    setEditingRegion(null);
    setRegionValue("");
    setRegionError("");
  }, []);

  const handleRegionChange = useCallback((value) => {
    setRegionValue(value.toUpperCase());
    setRegionError("");
  }, []);

  const handleRegionSave = useCallback(async (monitorId) => {
    const value = regionValue.trim().toUpperCase();

    // Validate
    if (value && !isValidCountryCode(value)) {
      setRegionError("Invalid country code");
      return;
    }

    setSavingRegion(true);
    setRegionError("");

    try {
      const res = await fetch(API_MONITOR_REGION(monitorId), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ region: value || null }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Failed to update");
      }

      // Update parent state
      if (onRegionUpdate) {
        onRegionUpdate(monitorId, data.region);
      }

      setEditingRegion(null);
      setRegionValue("");
    } catch (error) {
      setRegionError(error.message || "Failed to save");
    } finally {
      setSavingRegion(false);
    }
  }, [regionValue, onRegionUpdate]);

  const handleRegionKeyDown = useCallback((e, monitorId) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleRegionSave(monitorId);
    } else if (e.key === "Escape") {
      handleRegionCancel();
    }
  }, [handleRegionSave, handleRegionCancel]);

  const regionEditor = useMemo(() => ({
    value: regionValue,
    error: regionError,
    saving: savingRegion,
    onChange: handleRegionChange,
    onKeyDown: handleRegionKeyDown,
    onSave: handleRegionSave,
  }), [regionValue, regionError, savingRegion, handleRegionChange, handleRegionKeyDown, handleRegionSave]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Filter monitors based on search and region
  const filteredMonitors = useMemo(() => {
    const needle = debouncedSearch.toLowerCase();
    return monitors.filter((monitor) => {
      const matchesSearch = monitor.name.toLowerCase().includes(needle);
      const matchesRegion = !selectedRegion || selectedRegion === "All Regions" ||
        (selectedRegion === "Not Set" ? !monitor.region : monitor.region === selectedRegion);
      return matchesSearch && matchesRegion;
    });
  }, [monitors, debouncedSearch, selectedRegion]);

  const pageCount = Math.max(1, Math.ceil(filteredMonitors.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);

  const visibleMonitors = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredMonitors.slice(start, start + PAGE_SIZE);
  }, [filteredMonitors, currentPage]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, selectedRegion, selectedCategory]);

  const toggleSelect = useCallback((id) => {
    onSelectionChange((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, [onSelectionChange]);

  return (
    <div>
      {/* Search Bar & Filter */}
      <div className="mb-3 d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div className="d-flex align-items-center gap-2">
          <FiMonitor className="text-gray fs-20" />
          <span className="text-white fw-500">
            {monitors.length} Monitor{monitors.length !== 1 ? "s" : ""}
          </span>
        </div>
        <div className="monitors-toolbar d-flex flex-wrap gap-2">
          {onOpenCategoryDefaults && (
            <Button
              variant="dark"
              className="d-inline-flex align-items-center gap-2 border-secondary text-nowrap"
              onClick={onOpenCategoryDefaults}
              style={{ height: "38px" }}
              title="Set default webhook per category"
            >
              <FiTag className="fs-14" />
              <span className="fs-14">Category Defaults</span>
            </Button>
          )}
          {/* Category Filter */}
          <div className="position-relative">
            <Button
              variant="dark"
              className="d-inline-flex align-items-center gap-2 border-secondary text-nowrap"
              onClick={() => setShowDropdown(!showDropdown)}
              style={{ height: "38px" }}
            >
              <FiFilter className="fs-14" />
              <span className="fs-14">{selectedCategory}</span>
            </Button>
            {showDropdown && (
              <div
                className="toolbar-dropdown position-absolute bg-dark border border-secondary rounded mt-1"
                style={{
                  right: 0,
                  zIndex: 1000,
                  minWidth: "150px",
                }}
              >
                {categories.map((category) => (
                  <div
                    key={category}
                    className={`px-3 py-2 text-white ${
                      selectedCategory === category ? "bg-primary" : ""
                    }`}
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setSelectedCategory(category);
                      setShowDropdown(false);
                    }}
                    onMouseEnter={(e) => {
                      if (selectedCategory !== category) {
                        e.currentTarget.style.backgroundColor = "#2a2a2a";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedCategory !== category) {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }
                    }}
                  >
                    {category}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Region Filter */}
          <div className="position-relative">
            <Button
              variant="dark"
              className="d-inline-flex align-items-center gap-2 border-secondary text-nowrap"
              onClick={() => setShowRegionDropdown(!showRegionDropdown)}
              style={{ height: "38px" }}
            >
              <FiGlobe className="fs-14" />
              <span className="fs-14">
                {selectedRegion === "All Regions" ? "All Regions" :
                 selectedRegion === "Not Set" ? "Not Set" :
                 `${countryCodeToFlag(selectedRegion)} ${selectedRegion}`}
              </span>
            </Button>
            {showRegionDropdown && (
              <div
                className="toolbar-dropdown position-absolute bg-dark border border-secondary rounded mt-1"
                style={{
                  right: 0,
                  zIndex: 1000,
                  minWidth: "150px",
                  maxHeight: "300px",
                  overflowY: "auto",
                }}
              >
                {regions.map((region) => (
                  <div
                    key={region}
                    className={`px-3 py-2 text-white ${
                      selectedRegion === region ? "bg-primary" : ""
                    }`}
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setSelectedRegion(region);
                      setShowRegionDropdown(false);
                    }}
                    onMouseEnter={(e) => {
                      if (selectedRegion !== region) {
                        e.currentTarget.style.backgroundColor = "#2a2a2a";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedRegion !== region) {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }
                    }}
                  >
                    {region === "All Regions" || region === "Not Set" ? region :
                     `${countryCodeToFlag(region)} ${region}`}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Search */}
          <InputGroup className="monitors-search">
            <InputGroup.Text className="bg-dark border-secondary">
              <FiSearch className="text-gray" />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search monitors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input-field border-secondary"
            />
          </InputGroup>
        </div>
      </div>

      {/* Table */}
      <div className="table-mobile-scroll">
      <div className="table-responsive">
        <Table className="table custom-table monitors-table-grid table-bordered" hover>
          <thead className="rounded-3">
            <tr>
              <th className="fs-13 fw-500 border-bottom text-center"></th>
              <th className="fs-13 fw-500 border-bottom">
                <div className="d-flex align-items-center gap-2">
                  <FiMonitor className="fs-16" />
                  Monitor Name
                </div>
              </th>
              <th className="fs-13 fw-500 border-bottom">
                <div className="d-flex align-items-center gap-2">
                  <FiPackage className="fs-16" />
                  Package
                </div>
              </th>
              <th className="fs-13 fw-500 border-bottom">
                <div className="d-flex align-items-center gap-2">
                  <FiCheckCircle className="fs-16" />
                  Status
                </div>
              </th>
              <th className="fs-13 fw-500 border-bottom">
                <div className="d-flex align-items-center gap-2">
                  <FiGlobe className="fs-16" />
                  Region
                </div>
              </th>
              <th className="table-action-btn fw-500 fs-13 text-end border-bottom">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="text-center text-white-65 py-5">
                  <div
                    className="spinner-border spinner-border-sm text-white me-2"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  Loading monitors...
                </td>
              </tr>
            ) : filteredMonitors.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center text-white-65 py-5">
                  {searchTerm ? (
                    <>No monitors found matching "{searchTerm}"</>
                  ) : (
                    <>No monitors found.</>
                  )}
                </td>
              </tr>
            ) : (
              visibleMonitors.map((monitor) => (
                <MonitorRow
                  key={monitor._id}
                  monitor={monitor}
                  isSelected={selectedMonitors.has(monitor._id)}
                  onToggleSelect={toggleSelect}
                  canEditCategory={canEditCategory}
                  isSavingCategory={savingCategory === monitor._id}
                  onCategorySave={handleCategorySave}
                  canEditRegion={canEditRegion}
                  onRegionEdit={handleRegionEdit}
                  regionEditor={
                    editingRegion === monitor._id ? regionEditor : null
                  }
                  onEdit={onEdit}
                  onWhitelist={onWhitelist}
                  onBlacklist={onBlacklist}
                />
              ))
            )}
          </tbody>
        </Table>
      </div>
      </div>

      {!loading && filteredMonitors.length > 0 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <span className="text-white-65 fs-13">
            Showing {(currentPage - 1) * PAGE_SIZE + 1}–
            {Math.min(currentPage * PAGE_SIZE, filteredMonitors.length)} of{" "}
            {filteredMonitors.length}
          </span>
          <div className="d-flex align-items-center gap-2">
            <Button
              variant="dark"
              size="sm"
              className="border-secondary"
              onClick={() => setPage(currentPage - 1)}
              disabled={currentPage <= 1}
            >
              <FiChevronLeft className="fs-14" />
            </Button>
            <span className="text-white fs-13">
              Page {currentPage} / {pageCount}
            </span>
            <Button
              variant="dark"
              size="sm"
              className="border-secondary"
              onClick={() => setPage(currentPage + 1)}
              disabled={currentPage >= pageCount}
            >
              <FiChevronRight className="fs-14" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonitorsTable;
